import { ChangeEventHandler, FocusEventHandler, useCallback, useRef } from 'react';

import { ITextAreaProps, Omit } from '@rbi-ctg/frontend';
import { isMobile } from 'utils/environment';

interface IUseTextAreaHandlesProps extends Omit<ITextAreaProps, 'value'> {
  textAreaId: string;
  queryType?: string;
}

interface ITextAreaHandles {
  handleBlur: FocusEventHandler<HTMLTextAreaElement>;
  handleChange: ChangeEventHandler<HTMLTextAreaElement>;
  handleFocus: FocusEventHandler<HTMLTextAreaElement>;
}

/**
 * @TODO: Combine this and `useInputHandles` https://github.com/rbilabs/EngineeringMonoRepo/pull/5133#discussion_r356262738
 */
export const useTextAreaHandles = ({
  textAreaId,
  onBlur,
  onChange,
  onFocus
}: IUseTextAreaHandlesProps): ITextAreaHandles => {
  const needsAutocompleteFocus = useRef(false);

  const handleChange = useCallback<ChangeEventHandler<HTMLTextAreaElement>>(
    e => {
      setTimeout(() => {
        try {
          const isAutoCompleted = !!document.querySelector(
            `textarea#${textAreaId}:-webkit-autofill`
          );

          if (isAutoCompleted) {
            needsAutocompleteFocus.current = true;
          }
        } catch (err) {
          // Do nothing
        }
      }, 1);

      if (onChange) {
        onChange(e);
      }
    },
    [textAreaId, onChange]
  );

  const handleBlur = useCallback<FocusEventHandler<HTMLTextAreaElement>>(
    e => {
      if (isMobile()) {
        try {
          const autoCompleteNode = document.querySelector(
            `textarea#${textAreaId}:-webkit-autofill`
          ) as HTMLTextAreaElement;

          if (!needsAutocompleteFocus.current) {
            return;
          }

          needsAutocompleteFocus.current = false;
          autoCompleteNode && autoCompleteNode.focus();
        } catch (error) {
          // Do nothing
        }
      }
      onBlur && onBlur(e);
    },
    [textAreaId, onBlur]
  );

  const handleFocus = useCallback<FocusEventHandler<HTMLTextAreaElement>>(
    event => {
      if (onFocus) {
        onFocus(event);
      }
    },
    [onFocus]
  );

  return {
    handleBlur,
    handleChange,
    handleFocus
  };
};
